import React, { useEffect, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';

import { setSessionExpired } from '~/helpers/sessions/slice';
import { useIdleSession } from './useIdleSession';
import { routes } from '~/helpers/routes';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

// Per this article: https://stackoverflow.com/questions/3468607
// setTimeout's millisecond delay maxes out at the highest number possible in 32-bit computing
export const SETTIMEOUT_MAXIMUM = 2147483647;

export const EVENT_LISTENER_DEBOUNCE = 1_000;

/**
 * Regex patterns matching routes where session timeout should not apply
 */
const NO_PROMPT_SESSION_TIMEOUT_ROUTES = [
  /\/journeys\/.+\/versions\/\d/,
  /\/applications\/.+\/versions\/\d/,
];

interface SessionManagementProps {
  /**
   * The number of seconds a user session can be idle for before expiring
   */
  sessionExpireAfter: number;
}

/**
 * Global modal component to manage user session timeout on inactivity
 */
const SessionManagement: React.FC<SessionManagementProps> = ({ sessionExpireAfter }) => {
  const session = useAppSelector(state => state.session);
  const dispatch = useAppDispatch();

  const { isIdle, setIsIdle, pingActivity } = useIdleSession({
    activityThrottleTimeMS: EVENT_LISTENER_DEBOUNCE,
    sessionExpireTimeMS: sessionExpireAfter * 1_000,
    onSessionExpire: () => {
      dispatch(setSessionExpired(true));
    },
  });

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const toggleWarningModal = (newValue?: boolean) => {
    setWarningModalOpen(preValue => newValue ?? !preValue);
  };

  useEffect(() => {
    if (isIdle) {
      toggleWarningModal(true);
    } else {
      toggleWarningModal(false);
    }
  }, [isIdle]);

  // TRIGGER EXPIRED SIDE EFFECTS
  useEffect(() => {
    // For some routes like the Journeys & Workflow Builders we do not want to
    // kick the user out here since both of those pages have their own
    // beforeunload handlers which we don't want to trigger in the case of a
    // session timeout - so we check that we're not on one of those pages
    const isNoPromptRoute = NO_PROMPT_SESSION_TIMEOUT_ROUTES.map(
      regex => regex.test(window.location.pathname),
    ).some(Boolean);

    if (!isNoPromptRoute && session.expired) {
      try {
        window.location.href = `${routes.logout(window.location.pathname)}`;
      } catch (err) {
        window.location.reload();
      }
    }
  }, [session.expired]);

  return (
    <Modal
      id="warning-modal"
      data-testid="SessionTimoutModal"
      isOpen={warningModalOpen}
      toggle={() => {
        toggleWarningModal();
        setIsIdle(false);
        pingActivity(0)
          .then(data => {
            if (data === 'logout') {
              dispatch(setSessionExpired(true));
            }
          })
          .catch(() => undefined);
      }}
      style={{ top: '200px' }}
    >
      <ModalBody style={{ textAlign: 'center' }}>
        <b>Your session is about to expire</b>
        <p>Click or type to extend your session</p>
      </ModalBody>
    </Modal>
  );
};

export default SessionManagement;
